import React, { useEffect, useState } from "react";
import submit from "submit-form";

import { BarGraphRow } from "./BarGraphRow";
import { MainSkeleton } from "./skeletons/MainSkeleton";
import { BackHandler } from "../../../../../parts/BackHandler";
import { MobileMainSkeleton } from "./skeletons/MobileMainSkeleton";

const mainDateRanges = [
  { label: "2 Weeks", value: 2, key: "two-weeks" },
  { label: "1 Month", value: 4, key: "one-month" },
  { label: "3 Months", value: 12, key: "three-months" },
  { label: "6 Months", value: 24, key: "six-months" },
  { label: "Lifetime", value: 0, key: "lifetime" },
];

export const MainGraph = ({
  groupedMetricsByProgram,
  setGroupedMetricsByProgram,
  locals,
}) => {
  const { programMetrics } = locals;
  const [backgroundSize, setBackgroundSize] = useState(100);
  const [maxValue, setMaxValue] = useState(100);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (programMetrics && loading) {
      setLoading(false);
    }
  }, [programMetrics]);

  // This state updates the main source of data for the horizontal bar graphs
  useEffect(() => {
    const groupedMetrics = programMetrics?.reduce((acc, curr) => {
      const { programId } = curr.metadata;
      if (acc[programId]) {
        acc[programId].programCount += 1;
      } else {
        acc[programId] = {
          programName: curr.metadata.programName,
          programCount: 1,
          programId,
        };
      }
      return acc;
    }, {});

    // Convert to array, sort by programCount, and convert back to object
    const sortedGroupedMetrics = Object.fromEntries(
      Object.entries(groupedMetrics).sort(
        ([, a], [, b]) => b.programCount - a.programCount
      )
    );

    setGroupedMetricsByProgram(sortedGroupedMetrics);
  }, [programMetrics]);

  // This effect establishes the max width of the graph based on the highest program count
  useEffect(() => {
    // Between all the program counts of each program
    const values = Object.values(groupedMetricsByProgram).map(
      ({ programCount }) => programCount
    );

    // We will find the biggest one and set it as a max value
    if (values?.length) {
      const max = Math.max(...values);
      // We will use this to split the space for the bars, where
      // MaxValue --> 100%
      // programCount --> X%
      setMaxValue(max);
    }
  }, [groupedMetricsByProgram]);

  return (
    <article role="program-metrics">
      <header>
        <BackHandler
          title={"Program Usage Metrics"}
          customBackUrl={"/coaching-portal/store"}
        />
        <div role="date-range-container">
          {mainDateRanges.map(({ label, value, key }) => {
            return (
              <button
                key={key}
                data-button-type={
                  key === locals.query.weekFrom ? "primary" : "secondary"
                }
                onClick={() => {
                  submit("/actions/refresh", {
                    method: "GET",
                    body: {
                      weekFrom: key,
                      lifetime: key === "lifetime",
                    },
                  });
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </header>
      <main role="program-metrics">
        <header>
          <span>Program Name</span>
          <span>Added to Calendar</span>
        </header>
        {loading ? (
          <>
            <MainSkeleton locals={locals} />
            <MobileMainSkeleton locals={locals} />
          </>
        ) : (
          <div role="h-bar-graph-wrapper">
            {Object.values(groupedMetricsByProgram).map(
              ({ programName, programCount, programId }, index) => {
                // Rule of three to calculate the width percentage (see the useEffect above)
                const widthPercentage = (programCount * 100) / maxValue;
                return (
                  <BarGraphRow
                    backgroundSize={backgroundSize}
                    key={programId}
                    maxValue={maxValue}
                    programCount={programCount}
                    programId={programId}
                    programName={programName}
                    setBackgroundSize={setBackgroundSize}
                    widthPercentage={widthPercentage}
                    locals={locals}
                  />
                );
              }
            )}
          </div>
        )}
      </main>
    </article>
  );
};
